import React from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import { Image } from 'expo-image';
import { useTheme, TYPOGRAPHY, BORDER_RADIUS } from '../../theme';
import { buildStaticMap } from '../../utils/staticMap';

interface Props {
  latitude: number;
  longitude: number;
  /** Rendered size of the square. The composite is scaled down to fit. */
  size?: number;
  zoom?: number;
}

/**
 * A small map of the property for the printed record.
 *
 * Plain images rather than Leaflet: the Print dialog does not wait for an
 * async tile layer, so a real map prints blank about as often as not. Nine
 * <img> tags are just images, and the browser blocks printing until they
 * load.
 */
export function StaticPropertyMap({ latitude, longitude, size = 320, zoom = 17 }: Props) {
  const { colors } = useTheme();
  const map = buildStaticMap(latitude, longitude, { zoom });
  const scale = size / map.width;

  return (
    <View style={styles.wrap}>
      <View
        style={[
          styles.frame,
          {
            width: size,
            height: size,
            borderColor: colors.border,
            backgroundColor: colors.surface,
          },
        ]}>
        <View
          style={[
            styles.canvas,
            {
              width: map.width,
              height: map.height,
              transform: [{ scale }],
              // Scale from the top-left so the tile offsets stay meaningful.
              ...(Platform.OS === 'web' ? { transformOrigin: 'top left' } : null),
            },
          ]}>
          {map.tiles.map((tile) => (
            <Image
              key={tile.url}
              source={tile.url}
              style={[styles.tile, { left: tile.left, top: tile.top }]}
              contentFit="cover"
              cachePolicy="memory-disk"
            />
          ))}
        </View>
        {/* Outside the scaled canvas on purpose: inside it the marker shrinks
            with the tiles and lands at about seven pixels, which reads as a
            speck rather than a pin. */}
        <View
          style={[
            styles.marker,
            {
              left: map.markerLeft * scale - MARKER / 2,
              top: map.markerTop * scale - MARKER / 2,
              borderColor: colors.background,
              backgroundColor: colors.action,
            },
          ]}
        />
      </View>
      <Text style={[styles.credit, { color: colors.textTertiary }]}>{map.attribution}</Text>
    </View>
  );
}

const MARKER = 18;

const styles = StyleSheet.create({
  wrap: { gap: 4 },
  frame: {
    overflow: 'hidden',
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: BORDER_RADIUS.sm,
  },
  canvas: { position: 'relative' },
  tile: { position: 'absolute', width: 256, height: 256 },
  marker: {
    position: 'absolute',
    width: MARKER,
    height: MARKER,
    borderRadius: BORDER_RADIUS.full,
    borderWidth: 3,
  },
  credit: { ...TYPOGRAPHY.caption },
});
